import { Button, Form, Input, Modal } from 'antd';
import { useState } from 'react';

interface DecisionFormValues {
  reason?: string;
}

interface CreditHoldDecisionModalProps {
  decision: 'approved' | 'held' | null;
  jobNumber: string;
  onCancel: () => void;
  onSubmit: (reason: string | undefined) => Promise<void>;
}

/**
 * The credit-hold decision modal (gitaumoses4/chukua#37) — split out of `JobCreditHoldPanel`,
 * same idiom as `JobActionReasonModal`. Reason is required on approval (it overrides a flag)
 * and optional on keeping the hold, so one modal serves both buttons.
 */
export function CreditHoldDecisionModal({ decision, jobNumber, onCancel, onSubmit }: CreditHoldDecisionModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<DecisionFormValues>();

  async function submit(values: DecisionFormValues) {
    setSubmitting(true);
    try {
      await onSubmit(values.reason || undefined);
      form.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  const approving = decision === 'approved';

  return (
    <Modal
      open={decision !== null}
      title={approving ? `Approve job ${jobNumber}` : `Keep holding job ${jobNumber}`}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form<DecisionFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="reason" label={approving ? 'Reason' : 'Reason (optional)'} rules={approving ? [{ required: true, message: 'Enter a reason' }] : []}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item className="mb-0 flex justify-end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
